import type { Doc } from '../types'

/**
 * A continuous stretch of editing collapses into one snapshot. Autosave fires
 * every quarter second; archiving each of those would store thousands of
 * near-identical revisions and make the history unreadable. Ten minutes gives
 * you the shape of a session without the noise.
 */
export const SNAPSHOT_INTERVAL = 10 * 60_000

/** Versions kept per note. Older ones are pruned as new ones arrive. */
export const MAX_VERSIONS = 50

/**
 * A note being edited for the first time in this session has no recorded
 * snapshot, and its state before the edit is exactly what is worth keeping —
 * so an unknown last-snapshot time always snapshots.
 */
export function shouldSnapshot(
  lastAt: number | undefined,
  now: number,
  interval = SNAPSHOT_INTERVAL,
): boolean {
  if (lastAt === undefined) return true
  return now - lastAt >= interval
}

/** Ids to delete so that at most `max` versions remain, oldest going first. */
export function prunable<T extends { id: string; savedAt: number }>(
  versions: T[],
  max = MAX_VERSIONS,
): string[] {
  if (versions.length <= max) return []
  return [...versions]
    .sort((a, b) => b.savedAt - a.savedAt)
    .slice(max)
    .map((v) => v.id)
}

/**
 * Structural equality over ProseMirror JSON. Reverting to a version, or moving
 * a note between folders, re-saves identical content; archiving that would fill
 * the history with duplicates of a single state.
 */
export function sameDoc(a: Doc | undefined, b: Doc | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return JSON.stringify(a) === JSON.stringify(b)
}
